<template>
  <div>
    <h4 class="mb-1">
      Непривязанные результаты
    </h4>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      {{ errorText }}
    </b-alert>
    <b-alert
      v-if="successText"
      show
      variant="success"
    >
      {{ successText }}
    </b-alert>
    <b-card no-body>
      <b-card-body>
        <div class="d-flex mb-1 align-items-start flex-wrap">
          <div
            class="mr-1 mb-50 unmatched-start-picker"
            style="min-width: 260px; flex: 1"
          >
            <v-select
              v-model="startOption"
              :filterable="false"
              :options="startSearchOptions"
              :clearable="true"
              label="label"
              placeholder="Название или ID старта"
              @search="onStartSearch"
              @input="onStartPicked"
            >
              <template #no-options>
                {{ startSearchHint }}
              </template>
            </v-select>
          </div>
          <b-form-input
            v-model="q"
            placeholder="ФИО в протоколе"
            class="mr-1 mb-50"
            style="min-width: 200px; flex: 1"
            @input="onFioInput"
          />
          <b-button
            variant="primary"
            class="mb-50"
            :disabled="loading"
            @click="fetch"
          >
            Найти
          </b-button>
        </div>
        <ul
          v-if="fioSuggestions.length"
          class="unmatched-fio-suggest list-unstyled mb-1"
        >
          <li
            v-for="item in fioSuggestions"
            :key="item.id + '-' + item.name"
            class="unmatched-fio-suggest__item"
            @click="pickFio(item)"
          >
            {{ item.name }}
            <small
              v-if="item.startName"
              class="text-muted"
            > · {{ item.startName }}</small>
          </li>
        </ul>
        <b-table
          :items="rows"
          :fields="fields"
          :busy="loading"
          striped
          responsive
          empty-text="Нет непривязанных результатов"
        >
          <template #cell(visibility)="data">
            <div>
              <b-badge v-if="data.item.visibility && data.item.visibility.linked" variant="success">Привязан</b-badge>
              <b-badge v-else variant="secondary">Не привязан</b-badge>
              <b-badge v-if="data.item.visibility && data.item.visibility.possibleMatchExists" variant="info">Есть match</b-badge>
              <b-badge v-if="data.item.visibility && data.item.visibility.greyProfileExists" variant="warning">Есть серый</b-badge>
              <b-badge v-if="data.item.visibility && data.item.visibility.needsClarification" variant="dark">Уточнение</b-badge>
            </div>
          </template>
          <template #cell(anomaly)="data">
            <b-badge
              v-if="data.item.birthYearAnomaly"
              variant="warning"
            >
              Аномальный год
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-50"
              @click="openSuggest(data.item)"
            >
              Предложить
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="createGrey(data.item)"
            >
              Создать серый профиль
            </b-button>
          </template>
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner small /> Загрузка...
            </div>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <b-modal
      id="suggest-modal"
      title="Предложить результат"
      ok-title="Отправить"
      cancel-title="Отмена"
      @ok="submitSuggest"
    >
      <p class="text-muted mb-1">
        Найдите живой аккаунт, которому отправить этот результат.
        Можно заполнить несколько полей сразу: тогда останутся люди, у которых совпадает и ФИО, и телефон.
        Серые профили здесь не ищутся.
      </p>
      <b-form-group
        label="ФИО"
        description="Имя и фамилия целиком или по отдельности, например: Шевель Николай"
      >
        <b-form-input
          v-model="suggestFio"
          placeholder="Шевель Николай"
          @input="onSuggestFioInput"
          @keyup.enter="searchUsers"
        />
        <ul
          v-if="suggestFioOptions.length"
          class="unmatched-fio-suggest list-unstyled mt-50 mb-0"
        >
          <li
            v-for="opt in suggestFioOptions"
            :key="opt.value"
            class="unmatched-fio-suggest__item"
            @click="pickSuggestUser(opt)"
          >
            {{ opt.text }}
          </li>
        </ul>
      </b-form-group>
      <b-form-group
        label="Телефон"
        description="Необязательно. Можно вместе с ФИО, чтобы сузить список"
      >
        <b-form-input
          v-model="suggestPhone"
          placeholder="+7 999 123-45-67"
          @keyup.enter="searchUsers"
        />
      </b-form-group>
      <b-form-group
        label="Email"
        description="Необязательно"
      >
        <b-form-input
          v-model="suggestEmail"
          placeholder="name@example.com"
          @keyup.enter="searchUsers"
        />
      </b-form-group>
      <b-button
        size="sm"
        class="mb-1"
        :disabled="searchingUsers"
        @click="searchUsers"
      >
        Искать
      </b-button>
      <p
        v-if="searchHint"
        class="text-muted"
      >
        {{ searchHint }}
      </p>
      <b-form-select
        v-if="userOptions.length"
        v-model="selectedUserId"
        :options="userOptions"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import _ from 'lodash'
import identityApi from '@/services/identity-api.service'

export default {
  name: 'UnmatchedResultsPage',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BSpinner,
    BTable,
    vSelect,
  },
  data() {
    return {
      loading: false,
      errorText: '',
      successText: '',
      q: '',
      startId: '',
      startOption: null,
      startSearchOptions: [],
      startSearchHint: 'Введите минимум 2 символа',
      fioSuggestions: [],
      suggestFioOptions: [],
      rows: [],
      selectedResult: null,
      suggestFio: '',
      suggestPhone: '',
      suggestEmail: '',
      selectedUserId: null,
      userOptions: [],
      searchingUsers: false,
      searchHint: '',
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'ФИО' },
        { key: 'birthday', label: 'ДР' },
        { key: 'start', label: 'Старт', formatter: value => (value && value.name) || '—' },
        { key: 'result', label: 'Результат' },
        { key: 'anomaly', label: 'Аномалия' },
        { key: 'visibility', label: 'Статус' },
        { key: 'actions', label: '' },
      ],
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    requestError(error, fallback) {
      if (error.response && error.response.status === 404) {
        return 'Сервер не нашёл API идентичности. Запустите локальный backend с этой фичей и направьте админку на него (VUE_APP_API_HOST), а не на api-dev без этих маршрутов.'
      }
      return (error.response && error.response.data && error.response.data.message) || fallback
    },
    formatStartOption(start) {
      if (!start) return null
      return {
        id: start.id,
        name: start.name,
        label: `${start.name} (#${start.id})`,
      }
    },
    onStartSearch(search, loading) {
      const query = (search || '').trim()
      if (!query || query.length < 2) {
        this.startSearchOptions = []
        this.startSearchHint = 'Введите минимум 2 символа'
        return
      }
      loading(true)
      this.searchStarts(query, loading)
    },
    searchStarts: _.debounce(async function searchStartsDebounced(search, loading) {
      try {
        const { data } = await this.$http.get('start/autocomplete', {
          params: { query: search, limit: 20 },
        })
        this.startSearchOptions = (data.suggestions || []).map(this.formatStartOption)
        this.startSearchHint = this.startSearchOptions.length ? '' : 'Старты не найдены'
      } catch (e) {
        this.startSearchOptions = []
        this.startSearchHint = 'Ошибка поиска'
      } finally {
        loading(false)
      }
    }, 350),
    onStartPicked(start) {
      this.startId = start && start.id ? String(start.id) : ''
    },
    onFioInput: _.debounce(function onFioInputDebounced() {
      this.loadFioSuggestions()
    }, 350),
    async loadFioSuggestions() {
      const q = (this.q || '').trim()
      if (q.length < 2) {
        this.fioSuggestions = []
        return
      }
      try {
        const startId = Number(this.startId)
        const { data } = await identityApi.getUnmatchedResults({
          q,
          startId: Number.isFinite(startId) && startId > 0 ? startId : undefined,
          maxResultCount: 12,
        })
        const seen = new Set()
        this.fioSuggestions = (data.rows || [])
          .map(row => {
            const name = (row.name || '').trim()
            if (!name || seen.has(name.toLowerCase())) return null
            seen.add(name.toLowerCase())
            return {
              id: row.id,
              name,
              startName: (row.start && row.start.name) || '',
            }
          })
          .filter(Boolean)
      } catch (e) {
        this.fioSuggestions = []
      }
    },
    pickFio(item) {
      this.q = item.name
      this.fioSuggestions = []
      this.fetch()
    },
    async fetch() {
      this.loading = true
      this.errorText = ''
      this.fioSuggestions = []
      try {
        const startId = Number(this.startId)
        const { data } = await identityApi.getUnmatchedResults({
          q: this.q || undefined,
          startId: Number.isFinite(startId) && startId > 0 ? startId : undefined,
          maxResultCount: 50,
        })
        this.rows = data.rows || []
      } catch (e) {
        this.errorText = this.requestError(e, 'Ошибка загрузки')
      } finally {
        this.loading = false
      }
    },
    openSuggest(item) {
      this.selectedResult = item
      this.suggestFio = (item && item.name) || ''
      this.suggestPhone = ''
      this.suggestEmail = ''
      this.selectedUserId = null
      this.userOptions = []
      this.suggestFioOptions = []
      this.searchHint = 'Заполните поля и нажмите «Искать». Можно оставить только ФИО или добавить телефон.'
      this.$bvModal.show('suggest-modal')
      if (this.suggestFio.trim()) {
        this.searchUsers()
      }
    },
    onSuggestFioInput: _.debounce(function onSuggestFioInputDebounced() {
      this.searchUsers()
    }, 400),
    pickSuggestUser(opt) {
      this.selectedUserId = opt.value
      const [fioOnly] = opt.text.split(' (#')
      this.suggestFio = fioOnly
      this.suggestFioOptions = []
      this.userOptions = [opt]
    },
    async searchUsers() {
      if (!this.suggestFio.trim() && !this.suggestPhone.trim() && !this.suggestEmail.trim()) {
        this.searchHint = 'Введите хотя бы ФИО, телефон или email'
        this.suggestFioOptions = []
        return
      }
      this.searchingUsers = true
      this.searchHint = ''
      try {
        const { data } = await identityApi.searchUsers({
          fio: this.suggestFio.trim() || undefined,
          phone: this.suggestPhone.trim() || undefined,
          email: this.suggestEmail.trim() || undefined,
        })
        this.userOptions = (data.rows || []).map(user => ({
          value: user.id,
          text: `${user.surname} ${user.name} (#${user.id})${user.birthday ? `, ${user.birthday}` : ''}`,
        }))
        this.suggestFioOptions = this.userOptions.slice(0, 8)
        this.selectedUserId = this.userOptions.length === 1 ? this.userOptions[0].value : null
        this.searchHint = this.userOptions.length
          ? 'Выберите человека в списке и нажмите «Отправить».'
          : 'Никого не нашли. Проверьте ФИО и телефон или предложите другому аккаунту.'
      } catch (e) {
        this.searchHint = (e.response && e.response.data && e.response.data.message) || 'Поиск не удался'
      } finally {
        this.searchingUsers = false
      }
    },
    async submitSuggest(evt) {
      evt.preventDefault()
      if (!this.selectedUserId || !this.selectedResult) {
        this.errorText = 'Выберите пользователя'
        return
      }
      try {
        await identityApi.suggestResult({ resultId: this.selectedResult.id, targetUserId: this.selectedUserId })
        this.successText = 'Предложение отправлено'
        this.$bvModal.hide('suggest-modal')
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Не удалось предложить результат'
      }
    },
    confirmDialog(text, options = {}) {
      return this.$bvModal.msgBoxConfirm(text, {
        title: options.title || 'Подтверждение',
        okTitle: options.okTitle || 'Продолжить',
        cancelTitle: 'Отмена',
        okVariant: options.okVariant || 'primary',
        centered: true,
        hideHeaderClose: false,
      })
    },
    similarGreyText(data) {
      const names = (data.similar || [])
        .map(item => [item.surname, item.name].filter(Boolean).join(' '))
        .filter(Boolean)
        .join(', ')
      if (names) {
        return `Уже есть похожие серые профили: ${names}. Создать ещё один?`
      }
      return data.warning || 'Найдены похожие серые профили. Создать ещё один?'
    },
    async createGrey(item) {
      const first = await this.confirmDialog('Создать серый профиль из этого результата?', {
        title: 'Серый профиль',
        okTitle: 'Создать',
      })
      if (!first) return
      try {
        const { data } = await identityApi.createGreyFromResult({ resultId: item.id })
        if (data.warning) {
          const force = await this.confirmDialog(this.similarGreyText(data), {
            title: 'Похожие профили',
            okTitle: 'Создать всё равно',
          })
          if (!force) return
          await identityApi.createGreyFromResult({ resultId: item.id, force: true })
        }
        this.successText = 'Серый профиль создан'
        this.fetch()
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Не удалось создать профиль'
      }
    },
  },
}
</script>

<style scoped>
.unmatched-fio-suggest {
  border: 1px solid #d8d6de;
  border-radius: 6px;
  max-height: 220px;
  overflow: auto;
  background: #fff;
}
.unmatched-fio-suggest__item {
  padding: 8px 12px;
  cursor: pointer;
}
.unmatched-fio-suggest__item:hover {
  background: #f3f2f7;
}
.unmatched-start-picker >>> .vs__dropdown-toggle {
  min-height: 38px;
}
</style>
